/**
 * TabbedActivityPanel - SESSION 111
 * 
 * Combines Tape Log and Alerts into a single tabbed panel
 * to save vertical space in the right sidebar.
 */

import React, { useState } from 'react';
import { Activity, Bell } from 'lucide-react';

export const TabbedActivityPanel = ({ tape = [], alerts = [] }) => {
  const [activeTab, setActiveTab] = useState('tape');
  
  // Count unread/recent alerts
  const recentAlertCount = alerts.filter(a => {
    const alertTime = new Date(a.timestamp);
    const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
    return alertTime > fiveMinAgo;
  }).length;

  return (
    <div className="panel h-[200px] flex flex-col" data-testid="tabbed-activity-panel">
      {/* Tab Headers */}
      <div className="flex border-b border-[var(--border-color)]">
        <button
          onClick={() => setActiveTab('tape')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${
            activeTab === 'tape'
              ? 'text-[var(--accent-primary)] border-b-2 border-[var(--accent-primary)] bg-[var(--accent-primary)]/5'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
          data-testid="tab-tape"
        >
          <Activity className="w-3.5 h-3.5" />
          Tape Log
          {tape.length > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400">
              {tape.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('alerts')}
          className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold transition-colors ${
            activeTab === 'alerts'
              ? 'text-[var(--accent-primary)] border-b-2 border-[var(--accent-primary)] bg-[var(--accent-primary)]/5'
              : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
          }`}
          data-testid="tab-alerts"
        >
          <Bell className="w-3.5 h-3.5" />
          Alerts
          {recentAlertCount > 0 && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-amber-500/20 text-amber-400 animate-pulse">
              {recentAlertCount}
            </span>
          )}
        </button>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin">
        {activeTab === 'tape' ? (
          <TapeLogContent tape={tape} />
        ) : (
          <AlertsContent alerts={alerts} />
        )}
      </div>
    </div>
  );
};

// Tape Log Content
const TapeLogContent = ({ tape }) => {
  if (!tape || tape.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--text-muted)] text-xs">
        No tape data
      </div>
    );
  }

  return (
    <div className="p-2 space-y-1">
      {tape.slice(0, 15).map((item, idx) => (
        <div
          key={idx}
          className="flex items-center justify-between text-[10px] px-2 py-1 rounded bg-[var(--bg-surface)]/50 hover:bg-[var(--bg-surface)]"
        >
          <span className="text-[var(--text-muted)] font-mono">
            {item.time || '--:--'}
          </span>
          <span className={`font-semibold ${
            item.type === 'BUY' ? 'text-green-400' : 
            item.type === 'SELL' ? 'text-red-400' : 'text-[var(--text-secondary)]'
          }`}>
            {item.type || 'TRADE'}
          </span>
          <span className="font-mono text-[var(--text-primary)]">
            {item.price?.toLocaleString() || '--'}
          </span>
          <span className="text-[var(--text-muted)]">
            {item.qty?.toLocaleString() || '--'}
          </span>
        </div>
      ))}
    </div>
  );
};

// Alerts Content
const AlertsContent = ({ alerts }) => {
  if (!alerts || alerts.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[var(--text-muted)] text-xs">
        No alerts
      </div>
    );
  }

  const getAlertStyle = (type) => {
    switch (type?.toUpperCase()) {
      case 'SIGNAL':
        return 'border-l-green-500 bg-green-500/5';
      case 'WARNING':
        return 'border-l-amber-500 bg-amber-500/5';
      case 'ERROR':
        return 'border-l-red-500 bg-red-500/5';
      case 'INFO':
      default:
        return 'border-l-cyan-500 bg-cyan-500/5';
    }
  };

  return (
    <div className="p-2 space-y-1">
      {alerts.slice(0, 10).map((alert, idx) => (
        <div
          key={idx}
          className={`border-l-2 px-2 py-1.5 rounded-r text-[10px] ${getAlertStyle(alert.type)}`}
        >
          <div className="flex items-center justify-between mb-0.5">
            <span className="font-semibold text-[var(--text-primary)]">
              {alert.title || alert.type || 'Alert'}
            </span>
            <span className="text-[var(--text-muted)] text-[9px]">
              {alert.time || new Date(alert.timestamp).toLocaleTimeString('en-IN', { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </span>
          </div>
          <p className="text-[var(--text-secondary)] line-clamp-2">
            {alert.message || alert.description || '--'}
          </p>
        </div>
      ))}
    </div>
  );
};

export default TabbedActivityPanel;
